import { useEffect, useState } from "react";
import axios from "axios";
import { Upload, Trash2, FileText, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const KnowledgeBase = () => {
  const [docs, setDocs] = useState([]);
  const [titolo, setTitolo] = useState("");
  const [testo, setTesto] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/knowledge`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!titolo.trim() || (!file && !testo.trim())) {
      toast.error("Servono un titolo e un file (o testo incollato).");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("titolo", titolo);
    if (file) fd.append("file", file);
    else fd.append("testo", testo);
    try {
      await axios.post(`${API}/knowledge`, fd);
      toast.success("Documento aggiunto: l'assistente lo usa da subito.");
      setTitolo("");
      setTesto("");
      setFile(null);
      load();
    } catch {
      toast.error("Errore di caricamento. Controlla il formato del file.");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await axios.delete(`${API}/knowledge/${id}`).catch(() => {});
    load();
  };

  return (
    <section className="mt-12" data-testid="knowledge-section">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-xmove-orange">
        <BookOpen size={14} /> Base di conoscenza
      </p>
      <h2 className="mt-2 font-display text-2xl font-bold text-xmove-ink">Istruisci l'assistente coi tuoi file</h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-xmove-muted">
        Carica listini, condizioni, policy o FAQ interne (PDF, Word, TXT, CSV): l'assistente in chat
        risponderà attingendo prima di tutto da questi documenti. Modifiche attive immediatamente.
      </p>

      <form onSubmit={add} className="mt-6 rounded-2xl border border-xmove-ink/8 bg-white p-6 shadow-soft" data-testid="knowledge-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-xmove-muted">Titolo documento *</span>
            <input value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Es. Condizioni commerciali 2026" data-testid="knowledge-titolo-input" className="w-full rounded-xl border border-xmove-ink/15 bg-xmove-cream px-4 py-3 text-sm outline-none focus:border-xmove-orange" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-xmove-muted">File (PDF, DOCX, TXT, CSV)</span>
            <span className="flex items-center gap-2 rounded-xl border border-dashed border-xmove-ink/25 bg-xmove-cream px-4 py-3 text-sm text-xmove-muted">
              <Upload size={15} className="text-xmove-orange" />
              <input type="file" accept=".pdf,.docx,.txt,.md,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="knowledge-file-input" className="w-full text-xs file:mr-3 file:rounded-lg file:border-0 file:bg-xmove-orange file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-white" />
            </span>
          </label>
        </div>
        {!file && (
          <label className="mt-4 block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-xmove-muted">Oppure incolla il testo</span>
            <textarea value={testo} onChange={(e) => setTesto(e.target.value)} rows={4} placeholder="Incolla qui condizioni, listini, risposte standard…" data-testid="knowledge-testo-input" className="w-full rounded-xl border border-xmove-ink/15 bg-xmove-cream px-4 py-3 text-sm outline-none focus:border-xmove-orange" />
          </label>
        )}
        <button type="submit" disabled={saving} data-testid="knowledge-add-btn" className="mt-4 flex items-center gap-2 rounded-xl bg-xmove-orange px-6 py-3 text-sm font-bold text-white shadow-soft transition-all hover:bg-xmove-orangeDark disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          Aggiungi alla conoscenza
        </button>
      </form>

      {docs.length > 0 && (
        <ul className="mt-6 space-y-2" data-testid="knowledge-list">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-xmove-ink/8 bg-white px-4 py-3">
              <span className="flex items-center gap-2.5 text-sm">
                <FileText size={16} className="shrink-0 text-xmove-orange" />
                <span className="font-semibold text-xmove-ink">{d.titolo}</span>
                <span className="text-xs text-xmove-muted">{d.chars.toLocaleString("it-IT")} caratteri</span>
              </span>
              <button onClick={() => del(d.id)} data-testid={`knowledge-delete-${d.id}`} aria-label={`Elimina ${d.titolo}`} className="text-xmove-muted transition-colors hover:text-red-500">
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
